import MenuIcon from '@mui/icons-material/Menu';
import {
  Box,
  Divider,
  Drawer as MuiDrawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import SupportModal from '../../redux/containers/support/SupportModal';
import AppText from '../utils/AppText';

const COLLAPSED_W = 64;
const EXPANDED_W = 216;

// `open` on a permanent Drawer is ignored by MUI — we repurpose it for width transitions.
const PermanentDrawer = styled(MuiDrawer)(({ theme, open }) => ({
  width: open ? EXPANDED_W : COLLAPSED_W,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  transition: 'width 0.2s ease',
  '& .MuiDrawer-paper': {
    width: open ? EXPANDED_W : COLLAPSED_W,
    transition: 'width 0.2s ease',
    overflowX: 'hidden',
    backgroundColor: theme.bruno.chrome.sidebarBackground,
    borderRight: `1px solid ${theme.palette.mode === 'dark' ? theme.bruno.chrome.menuDivider : 'rgba(255,255,255,0.08)'}`,
    display: 'flex',
    flexDirection: 'column',
  },
}));

function NavItem({ item, expanded, isActive, styles, onClick }) {
  const { t } = useTranslation();
  const label = t(`bruno.drawer.${item.name}`, item.label);

  return (
    <ListItem disablePadding sx={{ display: 'block', px: 1, mb: 0.5 }}>
      <Tooltip title={label} placement="right" disableHoverListener={expanded} arrow>
        <ListItemButton
          onClick={onClick}
          selected={isActive}
          aria-label={label}
          sx={{
            minHeight: 40,
            borderRadius: '8px',
            px: 0,
            justifyContent: 'flex-start',
            '&.Mui-selected': { bgcolor: styles.activeBg },
            '&.Mui-selected:hover': { bgcolor: styles.activeBg },
            '&:hover': { bgcolor: styles.hoverBg },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              width: COLLAPSED_W - 16,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconComponent
              iconName={item.icon}
              style={{ fontSize: 18, color: isActive ? styles.activeFg : styles.fg }}
            />
          </ListItemIcon>
          <AppText
            variant="body"
            style={{
              fontSize: 13,
              fontWeight: isActive ? 600 : 400,
              color: isActive ? styles.activeFg : styles.fg,
              opacity: expanded ? 1 : 0,
              transition: 'opacity 0.15s ease',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {label}
          </AppText>
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
}

function SidebarInner({ expanded, showHeader, onHamburgerClick, views, styles, navigate, location, onSupportClick }) {
  return (
    <>
      {/* Hamburger header — desktop only */}
      {showHeader && (
        <>
          <Box
            sx={{
              height: 56,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <IconButton
              onClick={onHamburgerClick}
              aria-label="toggle sidebar"
              size="small"
              sx={{ color: styles.fg, '&:hover': { bgcolor: styles.hoverBg } }}
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
          <Divider sx={{ borderColor: styles.divider }} />
        </>
      )}

      {/* Primary nav */}
      <List sx={{ flexGrow: 1, pt: showHeader ? 1 : 2 }}>
        {(views || []).map(item => (
          <NavItem
            key={item.path}
            item={item}
            expanded={expanded}
            isActive={location.pathname === item.path}
            styles={styles}
            onClick={() => navigate(item.path)}
          />
        ))}
      </List>

      {/* Footer — Support */}
      <Divider sx={{ borderColor: styles.divider }} />
      <List sx={{ pb: 1 }}>
        <NavItem
          item={{ path: '__support', icon: 'help-circle-outline', name: 'support', label: 'Support' }}
          expanded={expanded}
          isActive={false}
          styles={styles}
          onClick={onSupportClick}
        />
      </List>
    </>
  );
}

export default function Sidebar({ mobileOpen, onMobileClose, views }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [pinned, setPinned] = useState(() => {
    try { return localStorage.getItem('sidenav.expanded') === 'true'; } catch { return false; }
  });
  const [supportOpen, setSupportOpen] = useState(false);

  const handleHamburgerClick = () => {
    const next = !pinned;
    setPinned(next);
    try { localStorage.setItem('sidenav.expanded', String(next)); } catch {}
  };

  const styles = {
    fg: theme.bruno.chrome.sidebarForeground,
    activeBg: theme.bruno.chrome.sidebarActiveBackground,
    activeFg: theme.bruno.chrome.sidebarActiveForeground,
    hoverBg: theme.bruno.chrome.sidebarHover,
    divider: theme.palette.mode === 'dark' ? theme.bruno.chrome.menuDivider : 'rgba(255,255,255,0.1)',
  };

  const innerProps = {
    views,
    styles,
    navigate,
    location,
    onSupportClick: () => setSupportOpen(true),
  };

  const paperSx = {
    backgroundColor: theme.bruno.chrome.sidebarBackground,
    borderRight: `1px solid ${styles.divider}`,
    width: EXPANDED_W,
  };

  return (
    <>
      {isMobile ? (
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileClose}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: paperSx }}
        >
          <SidebarInner {...innerProps} expanded showHeader={false} />
        </MuiDrawer>
      ) : (
        <PermanentDrawer
          variant="permanent"
          open={pinned}
        >
          <SidebarInner
            {...innerProps}
            expanded={pinned}
            showHeader
            onHamburgerClick={handleHamburgerClick}
          />
        </PermanentDrawer>
      )}
      <SupportModal open={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
}
